import { IPromise } from 'angular';

import { Messages } from '../../codegen/Localize';
import { WebClientDetailFetcher } from '../../commonInfra/utils/resourceFetchers/WebClientDetailFetcher';
import { WebClientListResourceFetcher } from '../../commonInfra/utils/resourceFetchers/WebClientListResourceFetcher';
import WebHttpClient from '../../httpClient/WebHttpClient';
import { WebHttpClientWithErrorToastInstance } from '../../httpClient/WebHttpClientWithErrorToast';
import { IDetailFetcher } from '../../ts/fetchers/IDetailFetcher';
import { IListFetcher } from '../../ts/fetchers/IListFetcher';
import { RequestHelper } from '../../httpClient/utils/RequestHelper';
import { ResultHelper } from '../../httpClient/utils/ResultHelper';
import { ServerApiMapper } from '../../ts/helpers/ServerModelHelper';
import { PublishedConnectionMapper } from '../../ts/mappers/PublishedConnectionMapper';
import { IPublishedConnectionDetailInfo, IPublishedConnectionInfo } from '../../ts/models/IPublishedConnectionInfo';
import { FieldValuesQuery } from '../../ts/resources/FieldValuesQuery';
import { IActionResource, IDetailResource, IFieldValuesResource, IListResource } from '../../ts/resources/Resource';
import { ResourceQuery } from '../../ts/resources/ResourceQuery';
import { AddTagsToPublishedConnectionsRequest } from '../../ts/ServerApi/requests/AddTagsToPublishedConnectionsRequest';
import { DeletePublishedConnectionsRequest } from '../../ts/ServerApi/requests/DeletePublishedConnectionsRequest';
import { GetFieldValuesRequest } from '../../ts/ServerApi/requests/GetFieldValuesRequest';
import { GetPublishedConnectionActionsRequest } from '../../ts/ServerApi/requests/GetPublishedConnectionActionsRequest';
import { GetPublishedConnectionsRequest } from '../../ts/ServerApi/requests/GetPublishedConnectionsRequest';
import { MarkRecentlyViewedRequest, RecentlyViewedObjectType } from '../../ts/ServerApi/requests/MarkRecentlyViewedRequest';
import { MovePublishedConnectionsToProject } from '../../ts/ServerApi/requests/MovePublishedConnectionsToProjectRequest';
import {
  RemoveTagsFromPublishedConnectionsRequest,
} from '../../ts/ServerApi/requests/RemoveTagsFromPublishedConnectionsRequest';
import {
  IPublishedConnectionDescriptionParams,
  SetPublishedConnectionDescriptionRequest,
} from '../../ts/ServerApi/requests/SetPublishedConnectionDescriptionRequest';
import { SetPublishedConnectionNameRequest } from '../../ts/ServerApi/requests/SetPublishedConnectionNameRequest';
import { SetPublishedConnectionsOwnerRequest } from '../../ts/ServerApi/requests/SetPublishedConnectionsOwnerRequest';
import { IFieldValue } from '../../ts/ServerApi/types/IFieldValue';
import { IPublishedConnectionActions } from '../../ts/ServerApi/types/IPublishedConnectionActions';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { ToasterService, ToastType } from '../../ts/services/ToasterService';

class PublishedConnectionsResource implements IListResource<IPublishedConnectionInfo>, IFieldValuesResource, IDetailResource<IPublishedConnectionDetailInfo>, IActionResource<IPublishedConnectionActions> {

  private listFetcher: IListFetcher<IPublishedConnectionInfo> = new WebClientListResourceFetcher(GetPublishedConnectionsRequest, ServerApiMapper.publishedConnections);
  private detailFetcher: IDetailFetcher<IPublishedConnectionDetailInfo> = new WebClientDetailFetcher('getPublishedConnection', PublishedConnectionMapper.mapDetail)
  private fieldValuesFetcher: IListFetcher<IFieldValue> = new WebClientListResourceFetcher(GetFieldValuesRequest, ServerApiMapper.fieldValues);

  public all(): ResourceQuery<IPublishedConnectionInfo> {
    return new ResourceQuery(this.listFetcher);
  }

  public getById(id: string): Promise<IPublishedConnectionDetailInfo> {
    return this.detailFetcher.fetchById(id);
  }

  public valuesForField(field: string): FieldValuesQuery {
    return new FieldValuesQuery(this.fieldValuesFetcher, field, 'publishedConnection');
  }

  // Actions
  public actions(id: string): ng.IPromise<IPublishedConnectionActions> {
    return WebHttpClient.sendRequest(new GetPublishedConnectionActionsRequest(id));
  }

  public rename(id: string, name: string): ng.IPromise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(new SetPublishedConnectionNameRequest({ id: id, name: name }));
  }

  public del(ids: string[]): IPromise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new DeletePublishedConnectionsRequest(idsBlock)),
      ResultHelper.combineResults);
  }

  public setOwner(ids: string[], ownerId: string): IPromise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new SetPublishedConnectionsOwnerRequest(idsBlock, ownerId)),
      ResultHelper.combineResults);
  }

  public moveToProject(targetProjectId: string, publishedConnectionIds: string[]): IPromise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(publishedConnectionIds,
        (idsBlock) => new MovePublishedConnectionsToProject({
          ids: idsBlock,
          projectId: targetProjectId
        })),
      ResultHelper.combineResults);
  }

  public addTags(ids: string[], tags: string[]): IPromise<IResult> {
    return (tags.length > 0)
      ? WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
          RequestHelper.buildRequests2(ids, tags,
            (idsBlock, tagsBlock) => new AddTagsToPublishedConnectionsRequest(idsBlock, tagsBlock)),
          ResultHelper.combineResults)
      : Promise.resolve({}) as unknown as IPromise<IResult>;
  }

  public removeTags(ids: string[], tags: string[]): IPromise<IResult> {
    return (tags.length > 0)
      ? WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
          RequestHelper.buildRequests2(ids, tags,
            (idsBlock, tagsBlock) => new RemoveTagsFromPublishedConnectionsRequest(idsBlock, tagsBlock)),
          ResultHelper.combineResults)
      : Promise.resolve({}) as unknown as IPromise<IResult>;
  }

  public changeTags(ids: string[], tagsToAdd: string[], tagsToRemove: string[]): IPromise<IResult> {
    return this.removeTags(ids, tagsToRemove)
      .then((result: IResult) => (ResultHelper.hasErrors(result) ? result : this.addTags(ids, tagsToAdd)));
  }

  public setDescription = async (args: IPublishedConnectionDescriptionParams): Promise<IResult> => {
    return WebHttpClientWithErrorToastInstance
      .sendRequestAndDelay(new SetPublishedConnectionDescriptionRequest(args))
      .then(r => this.toast(r, Messages.editDescriptionSuccessful(), Messages.editDescriptionFailure()));
  }

  public markRecentlyViewed(id: string): IPromise<IResult> {
    return WebHttpClient.sendRequest(new MarkRecentlyViewedRequest({
      objectId: id,
      objectType: RecentlyViewedObjectType.PublishedConnection
    }));
  }

  // Toast
  public toast(result: IResult, successMessage: string, failureMessage: string): any {
    if (result.errors && result.errors.length > 0) {
      ToasterService.instance.pop({
        type: ToastType.Error,
        text: failureMessage
      });
    } else {
      ToasterService.instance.pop({
        type: ToastType.Success,
        text: successMessage
      });
    }
  }
}

export function isPublishedConnectionInfo(item: any): item is IPublishedConnectionInfo {
  return item &&
         item.contentType &&
         item.contentType === 'publishedConnection';
}

export const PublishedConnections = new PublishedConnectionsResource();
